import { useState, useEffect } from 'react';
import { FiFile, FiFileText, FiImage } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeCsv } from 'react-icons/bs';
import { adminAPI } from '../lib/api';
import api from '../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [autoProcessing, setAutoProcessing] = useState(false);

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    if (ext === 'pdf') {
      return <AiOutlineFilePdf className="w-5 h-5 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <AiOutlineFileWord className="w-5 h-5 text-blue-500" />;
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return <AiOutlineFileExcel className="w-5 h-5 text-green-600" />;
    }
    if (ext === 'csv') {
      return <BsFiletypeCsv className="w-5 h-5 text-green-600" />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <FiImage className="w-5 h-5 text-purple-500" />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <AiOutlineFileZip className="w-5 h-5 text-yellow-600" />;
    }
    if (['txt', 'log', 'md'].includes(ext)) {
      return <FiFileText className="w-5 h-5 text-gray-500" />;
    }
    
    return <FiFile className="w-5 h-5 text-gray-400" />;
  };

  useEffect(() => {
    loadStats();
    loadHealth();
    // Refresh health every 30 seconds
    const interval = setInterval(loadHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDashboardStats();
      setStats(response.data || response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const response = await adminAPI.getSystemHealth();
      setHealth(response.data?.health || response.health);
    } catch (err) {
      console.error('Failed to load health:', err);
    }
  };

  const handleReprocessFailedDocuments = async () => {
    try {
      setReprocessing(true);
      setReprocessResult(null);
      setAutoProcessing(true);
      
      // Check if user is authenticated
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      // Start processing
      await processNextBatch();
      
    } catch (err) {
      console.error('Reprocessing error:', err);
      console.error('Error response:', err.response);
      
      let errorMessage = 'An unknown error occurred';
      if (err.response?.status === 401) {
        errorMessage = 'Unauthorized. Please log in as an admin.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Forbidden. Admin access required.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setReprocessResult({
        success: false,
        error: errorMessage,
      });
      setAutoProcessing(false);
    } finally {
      setReprocessing(false);
    }
  };

  const processNextBatch = async () => {
    try {
      const response = await api.post('/reprocess/failed');
      const data = response.data;
      
      setReprocessResult(data);
      setQueueStatus(data.queueStatus);
      
      // If there are more documents, continue processing after a short delay
      if (data.hasMore && autoProcessing) {
        console.log(`Processed ${data.processed} documents. ${data.queueStatus?.pending || 0} remaining. Continuing...`);
        setTimeout(() => {
          if (autoProcessing) {
            processNextBatch();
          }
        }, 2000); // 2 second delay between batches
      } else {
        console.log('Queue processing complete!');
        setAutoProcessing(false);
        await loadStats(); // Refresh stats when complete
      }
    } catch (err) {
      console.error('Batch processing error:', err);
      setAutoProcessing(false);
      throw err;
    }
  };

  const stopAutoProcessing = () => {
    setAutoProcessing(false);
  };

  const handleResetStuck = async () => {
    try {
      const response = await api.post('/reprocess/reset-stuck');
      alert(response.data.message);
      // Reload queue status after reset
      if (queueStatus) {
        handleReprocessFailedDocuments();
      }
    } catch (err) {
      console.error('Reset error:', err);
      alert('Failed to reset stuck items: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading dashboard: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <button
          onClick={loadStats}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          icon="👥"
          color="blue"
        />
        <StatCard
          title="Active Users (7 Days)"
          value={stats?.users?.active_7_days || 0}
          icon="✅"
          color="green"
        />
        <StatCard
          title="Total Documents"
          value={stats?.documents?.total || 0}
          icon="📄"
          color="blue"
        />
        <StatCard
          title="Active Folders"
          value={stats?.folders?.total || 0}
          icon="📁"
          color="green"
        />
        <StatCard
          title="AI Processed"
          value={stats?.documents?.ai_processed || 0}
          icon="🤖"
          color="purple"
        />
        <StatCard
          title="Total Storage"
          value={formatBytes(stats?.documents?.storage_bytes || 0)}
          icon="💾"
          color="orange"
        />
      </div>

      {/* Document Reprocessing */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Document Reprocessing Queue</h2>
            <p className="text-sm text-gray-600 mt-1">
              Automatically process all failed documents with AI extraction (PDFs, scanned images)
            </p>
          </div>
          <div className="flex gap-3">
            {queueStatus && queueStatus.processing > 0 && (
              <button
                onClick={handleResetStuck}
                className="px-4 py-2 rounded-lg font-medium bg-yellow-600 text-white hover:bg-yellow-700 text-sm"
              >
                Reset Stuck
              </button>
            )}
            {autoProcessing && (
              <button
                onClick={stopAutoProcessing}
                className="px-6 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Stop Processing
              </button>
            )}
            <button
              onClick={handleReprocessFailedDocuments}
              disabled={reprocessing || autoProcessing}
              className={`px-6 py-2 rounded-lg font-medium ${
                reprocessing || autoProcessing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {autoProcessing ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Auto-Processing...
                </span>
              ) : reprocessing ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Starting...
                </span>
              ) : (
              'Reprocess Failed Documents'
            )}
          </button>
          </div>
        </div>

        {/* Queue Status */}
        {queueStatus && (
          <div className="mb-4 grid grid-cols-4 gap-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-sm text-yellow-600 font-medium">Pending</div>
              <div className="text-2xl font-bold text-yellow-900">{queueStatus.pending || 0}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-sm text-blue-600 font-medium">Processing</div>
              <div className="text-2xl font-bold text-blue-900">{queueStatus.processing || 0}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-sm text-green-600 font-medium">Completed</div>
              <div className="text-2xl font-bold text-green-900">{queueStatus.completed || 0}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-sm text-red-600 font-medium">Failed</div>
              <div className="text-2xl font-bold text-red-900">{queueStatus.failed || 0}</div>
            </div>
          </div>
        )}

        {reprocessResult && (
          <div
            className={`mt-4 p-4 rounded-lg border ${
              reprocessResult.success
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {reprocessResult.success ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className={`text-sm font-medium ${
                  reprocessResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {reprocessResult.success ? 'Reprocessing Complete' : 'Reprocessing Failed'}
                </h3>
                {reprocessResult.success && (
                  <div className="mt-2 text-sm text-green-700">
                    <p className="font-medium">{reprocessResult.message}</p>
                    <div className="mt-2 space-y-1">
                      <p>• Total documents found: <span className="font-semibold">{reprocessResult.total}</span></p>
                      <p>• Successfully processed: <span className="font-semibold text-green-600">{reprocessResult.processed}</span></p>
                      {reprocessResult.failed > 0 && (
                        <p>• Failed: <span className="font-semibold text-red-600">{reprocessResult.failed}</span></p>
                      )}
                    </div>
                    {reprocessResult.errors && reprocessResult.errors.length > 0 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer font-medium hover:text-green-900">
                          View errors ({reprocessResult.errors.length})
                        </summary>
                        <ul className="mt-2 space-y-1 text-xs">
                          {reprocessResult.errors.map((err, idx) => (
                            <li key={idx} className="pl-4">
                              • {err.name || err.document_id}: {err.error}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                {!reprocessResult.success && (
                  <p className="mt-1 text-sm text-red-700">
                    {reprocessResult.error || 'An error occurred during reprocessing'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Security Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border-l-4 border-yellow-500 pl-4">
            <p className="text-sm text-gray-600">Failed Logins (24h)</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.security?.failed_logins_24h || 0}</p>
          </div>
          <div className="border-l-4 border-red-500 pl-4">
            <p className="text-sm text-gray-600">Active Lockouts</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.security?.active_lockouts || 0}</p>
          </div>
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm text-gray-600">Active Sessions</p>
            <p className="text-2xl font-bold text-gray-900">{stats?.security?.active_sessions || 0}</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Documents</h2>
          {stats?.recent_documents && stats.recent_documents.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_documents.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 py-2 border-b">
                  {/* File Type Icon */}
                  {getFileIcon(doc.name)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                    {formatBytes(doc.file_size)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No recent documents</p>
          )}
        </div>

        {/* Recent Users */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Users</h2>
          {stats?.recent_users && stats.recent_users.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_users.map((user) => (
                <div key={user.id} className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium text-gray-900">{user.full_name}</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No recent users</p>
          )}
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
        {health ? (
          <div className="space-y-4">
            <HealthIndicator
              label="Database Connection"
              status={health.database?.status || 'unknown'}
              message={health.database?.message || 'Checking...'}
              responseTime={health.database?.responseTime}
            />
            <HealthIndicator
              label="Storage Service"
              status={health.storage?.status || 'unknown'}
              message={health.storage?.message || 'Checking...'}
              responseTime={health.storage?.responseTime}
            />
            <HealthIndicator
              label="Authentication"
              status={health.authentication?.status || 'unknown'}
              message={health.authentication?.message || 'Checking...'}
              responseTime={health.authentication?.responseTime}
            />
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading health status...</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function HealthIndicator({ label, status, message, responseTime }) {
  const statusColors = {
    healthy: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-100 text-gray-800',
  };

  const dotColors = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    unknown: 'bg-gray-500',
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className={`w-3 h-3 rounded-full ${dotColors[status] || dotColors.unknown}`}></div>
        <div>
          <span className="font-medium text-gray-900">{label}</span>
          {responseTime !== undefined && responseTime > 0 && (
            <span className="text-xs text-gray-500 ml-2">({responseTime}ms)</span>
          )}
        </div>
      </div>
      <span className={`text-xs px-3 py-1 rounded-full ${statusColors[status] || statusColors.unknown}`}>
        {message}
      </span>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function getRoleBadgeColor(role) {
  const colors = {
    admin: 'bg-red-100 text-red-800',
    manager: 'bg-blue-100 text-blue-800',
    user: 'bg-gray-100 text-gray-800',
  };
  return colors[role] || colors.user;
}
